import {
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { DownloadFileNameLink, GreenText } from 'components'
import { BasicAddressDto } from 'dtos/addresses'
import { OhioTitleSelectedVehicleFormDto, OhioTitleVehicleDetailDto, OhioTitleVehicleDto } from 'dtos/ohioTitles'
import { useFormikContext } from 'formik'
import { useState } from 'react'
import { formatDate, formatDateMoment } from 'utils'
import {
  OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL,
  ohioTitlesCompactCellSx,
  ohioTitlesCompactSelectProps,
  ohioTitlesCompactTextFieldSx,
  ohioTitlesDateCellSx,
  ohioTitlesDocumentCellSx,
  ohioTitlesMissingCellSx,
  ohioTitlesPartyActionsCellSx,
  ohioTitlesPartyTableMergedSx,
  ohioTitlesRowLabelCellSx,
  ohioTitlesTableContainerSx,
  ohioTitlesTableSx
} from './ohioTitlesTableStyles'

export const formatBasicAddress = (address: BasicAddressDto): string => {
  let str = address.address1
  if (address.address2) str += ` ${address.address2}`
  str += `, ${address.city}, ${address.stateProvince} ${address.postalCode}`
  return str
}

interface BasicAddressSelectProps {
  onChange: (newValue: BasicAddressDto) => void
  options: BasicAddressDto[]
  value: BasicAddressDto | undefined
}

function BasicAddressSelect({ onChange, options, value }: BasicAddressSelectProps) {
  return (
    <TextField
      fullWidth
      margin='dense'
      onChange={e => onChange(JSON.parse(e.target.value))}
      select
      SelectProps={ohioTitlesCompactSelectProps}
      size='small'
      sx={ohioTitlesCompactTextFieldSx}
      value={value ? JSON.stringify(value) : undefined}
    >
      {options.map(option => (
        <MenuItem key={option.addressGuid} value={JSON.stringify(option)}>
          {formatBasicAddress(option)}
        </MenuItem>
      ))}
    </TextField>
  )
}

interface SelectedVehicleCardProps {
  form?: OhioTitleSelectedVehicleFormDto
  selected: OhioTitleVehicleDto
}

export default function SelectedVehicleCard({ selected, form }: SelectedVehicleCardProps) {
  const [ExporterCompanyAddress, setExporterCompanyAddress] = useState<BasicAddressDto | undefined>(undefined)
  const [BuyerCompanyAddress, setBuyerCompanyAddress] = useState<BasicAddressDto | undefined>(undefined)
  const [TitleDestinationCompanyAddress, setTitleDestinationCompanyAddress] = useState<BasicAddressDto | undefined>(undefined)
  const [VehicleDestinationCompanyAddress, setVehicleDestinationCompanyAddress] = useState<BasicAddressDto | undefined>(undefined)

  const { setFieldValue } = useFormikContext<OhioTitleVehicleDetailDto>()

  const addAsSeller = (name: string, address: BasicAddressDto) => {
    if (name) setFieldValue('ohioTitleSellerName', name)
    if (address.address2) setFieldValue('ohioTitleSellerAddress1', address.address1 + ' ' + address.address2)
    else setFieldValue('ohioTitleSellerAddress1', address.address1)
    setFieldValue('ohioTitleSellerCity', address.city)
    setFieldValue('ohioTitleSellerCounty', address.county?.trim() || '')
    setFieldValue('ohioTitleSellerCountry', address.country)
    setFieldValue('ohioTitleSellerStateProvince', address.stateProvince)
    setFieldValue('ohioTitleSellerPostalCode', address.postalCode)
  }

  const addAsOwner = (name: string, address: BasicAddressDto, irsNumber?: string, dealerNumber?: string) => {
    if (name) setFieldValue('ohioTitleOwnerName', name)
    if (address.address2) setFieldValue('ohioTitleOwnerAddress1', address.address1 + ' ' + address.address2)
    else setFieldValue('ohioTitleOwnerAddress1', address.address1)
    setFieldValue('ohioTitleOwnerCity', address.city)
    setFieldValue('ohioTitleOwnerCounty', address.county?.trim() || '')
    setFieldValue('ohioTitleOwnerCountry', address.country)
    setFieldValue('ohioTitleOwnerStateProvince', address.stateProvince)
    setFieldValue('ohioTitleOwnerPostalCode', address.postalCode)
    if (irsNumber) setFieldValue('ohioTitleOwnerIrsNumber', irsNumber)
    if (dealerNumber) setFieldValue('ohioTitleOwnerDealerNumber', dealerNumber)
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Selected Vehicle
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={ohioTitlesTableContainerSx}>
              <Table size='small' sx={ohioTitlesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Importer Stock #</TableCell>

                    <TableCell>PAPS #</TableCell>

                    <TableCell>Signed BOL</TableCell>

                    <TableCell>VIN</TableCell>

                    <TableCell>Year</TableCell>

                    <TableCell>Make</TableCell>

                    <TableCell>Model</TableCell>

                    <TableCell>7501</TableCell>

                    <TableCell>CAN Ownership</TableCell>

                    <TableCell>Vehicle Check-In</TableCell>

                    <TableCell>Sent to DOT</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell sx={ohioTitlesCompactCellSx}>{selected.importerStockNumber}</TableCell>

                    <TableCell sx={ohioTitlesCompactCellSx}>{selected.papsNumber ?? '-'}</TableCell>

                    <TableCell sx={ohioTitlesDateCellSx}>{selected.signedBOLDateTime ? formatDateMoment(selected.signedBOLDateTime) : '-'}</TableCell>

                    <TableCell sx={ohioTitlesCompactCellSx}>{selected.vin}</TableCell>

                    <TableCell sx={ohioTitlesCompactCellSx}>{selected.year}</TableCell>

                    <TableCell>{selected.make}</TableCell>

                    <TableCell>{selected.model}</TableCell>

                    {selected._7501FileName ? (
                      <TableCell sx={ohioTitlesDocumentCellSx}>
                        <DownloadFileNameLink fileName={selected._7501FileName} label={OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL} />
                      </TableCell>
                    ) : (
                      <TableCell sx={ohioTitlesMissingCellSx}>MISSING</TableCell>
                    )}

                    {selected.canOwnershipFileName ? (
                      <TableCell sx={ohioTitlesDocumentCellSx}>
                        <DownloadFileNameLink fileName={selected.canOwnershipFileName} label={OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL} />
                      </TableCell>
                    ) : (
                      <TableCell sx={ohioTitlesMissingCellSx}>MISSING</TableCell>
                    )}

                    <TableCell sx={ohioTitlesDateCellSx}>
                      {selected.vehicleCheckInDateTime ? formatDate(selected.vehicleCheckInDateTime) : '-'}
                    </TableCell>

                    <TableCell sx={ohioTitlesDateCellSx}>
                      {selected.sentToDOTDateTime ? formatDateMoment(selected.sentToDOTDateTime) : '-'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} sx={theme => ({ mt: theme.spacing(4) })}>
            <TableContainer sx={ohioTitlesTableContainerSx}>
              <Table size='small' sx={ohioTitlesPartyTableMergedSx}>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>

                    <TableCell>Name</TableCell>

                    <TableCell>Address</TableCell>

                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  <TableRow>
                    <TableCell sx={ohioTitlesRowLabelCellSx}>CAN Ownership</TableCell>

                    <TableCell>{form?.canOwnership?.name ?? '-'}</TableCell>

                    <TableCell>
                      {form && form.canOwnership && form.canOwnership.addresses.length === 1 ? formatBasicAddress(form.canOwnership.addresses[0]) : '-'}
                    </TableCell>

                    <TableCell align='right' sx={ohioTitlesPartyActionsCellSx}>
                      <Button
                        color='primary'
                        disabled={!form || !form.canOwnership || form.canOwnership.addresses.length !== 1}
                        onClick={() => addAsSeller(form?.canOwnership?.name!, form?.canOwnership?.addresses[0]!)}
                        size='small'
                        variant='text'
                        sx={theme => ({ mr: theme.spacing(4) })}
                      >
                        ADD AS SELLER
                      </Button>

                      <Button
                        color='primary'
                        disabled={!form || !form.canOwnership || form.canOwnership.addresses.length !== 1}
                        onClick={() => addAsOwner(form?.canOwnership?.name!, form?.canOwnership?.addresses[0]!)}
                        size='small'
                        variant='text'
                      >
                        ADD AS OWNER
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={ohioTitlesRowLabelCellSx}>Exporter Company</TableCell>

                    <TableCell>{form?.exporterCompany?.name ?? '-'}</TableCell>

                    <TableCell>
                      <BasicAddressSelect
                        onChange={value => setExporterCompanyAddress(value)}
                        options={form?.exporterCompany?.addresses ?? []}
                        value={ExporterCompanyAddress}
                      />
                    </TableCell>

                    <TableCell align='right' sx={ohioTitlesPartyActionsCellSx}>
                      <Button
                        color='primary'
                        disabled={!ExporterCompanyAddress}
                        onClick={() => addAsSeller(form?.exporterCompany?.name!, ExporterCompanyAddress!)}
                        size='small'
                        variant='text'
                        sx={theme => ({ mr: theme.spacing(4) })}
                      >
                        ADD AS SELLER
                      </Button>

                      <Button
                        color='primary'
                        disabled={!ExporterCompanyAddress}
                        onClick={() =>
                          addAsOwner(
                            form?.exporterCompany?.name!,
                            ExporterCompanyAddress!,
                            form?.exporterCompany?.irsNumber,
                            form?.exporterCompany?.dealerNumber
                          )
                        }
                        size='small'
                        variant='text'
                      >
                        ADD AS OWNER
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={ohioTitlesRowLabelCellSx}>Buyer Company</TableCell>

                    <TableCell>{form?.buyerCompany?.name ?? '-'}</TableCell>

                    <TableCell>
                      <BasicAddressSelect
                        onChange={value => setBuyerCompanyAddress(value)}
                        options={form?.buyerCompany?.addresses ?? []}
                        value={BuyerCompanyAddress}
                      />
                    </TableCell>

                    <TableCell align='right' sx={ohioTitlesPartyActionsCellSx}>
                      <Button
                        color='primary'
                        disabled={!BuyerCompanyAddress}
                        onClick={() => addAsSeller(form?.buyerCompany?.name!, BuyerCompanyAddress!)}
                        size='small'
                        variant='text'
                        sx={theme => ({ mr: theme.spacing(4) })}
                      >
                        ADD AS SELLER
                      </Button>

                      <Button
                        color='primary'
                        disabled={!BuyerCompanyAddress}
                        onClick={() => addAsOwner(form?.buyerCompany?.name!, BuyerCompanyAddress!)}
                        size='small'
                        variant='text'
                      >
                        ADD AS OWNER
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={ohioTitlesRowLabelCellSx}>Title Destination Company</TableCell>

                    <TableCell>{form?.titleDestinationCompany?.name ?? '-'}</TableCell>

                    <TableCell>
                      <BasicAddressSelect
                        onChange={value => setTitleDestinationCompanyAddress(value)}
                        options={form?.titleDestinationCompany?.addresses ?? []}
                        value={TitleDestinationCompanyAddress}
                      />
                    </TableCell>

                    <TableCell align='right' sx={ohioTitlesPartyActionsCellSx}>
                      <Button
                        color='primary'
                        disabled={!TitleDestinationCompanyAddress}
                        onClick={() => addAsSeller(form?.titleDestinationCompany?.name!, TitleDestinationCompanyAddress!)}
                        size='small'
                        variant='text'
                        sx={theme => ({ mr: theme.spacing(4) })}
                      >
                        ADD AS SELLER
                      </Button>

                      <Button
                        color='primary'
                        disabled={!TitleDestinationCompanyAddress}
                        onClick={() => addAsOwner(form?.titleDestinationCompany?.name!, TitleDestinationCompanyAddress!)}
                        size='small'
                        variant='text'
                      >
                        ADD AS OWNER
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={ohioTitlesRowLabelCellSx}>Vehicle Destination Company</TableCell>

                    <TableCell>{form?.vehicleDestinationCompany?.name ?? '-'}</TableCell>

                    <TableCell>
                      <BasicAddressSelect
                        onChange={value => setVehicleDestinationCompanyAddress(value)}
                        options={form?.vehicleDestinationCompany?.addresses ?? []}
                        value={VehicleDestinationCompanyAddress}
                      />
                    </TableCell>

                    <TableCell align='right' sx={ohioTitlesPartyActionsCellSx}>
                      <Button
                        color='primary'
                        disabled={!VehicleDestinationCompanyAddress}
                        onClick={() => addAsSeller(form?.vehicleDestinationCompany?.name!, VehicleDestinationCompanyAddress!)}
                        size='small'
                        variant='text'
                        sx={theme => ({ mr: theme.spacing(4) })}
                      >
                        ADD AS SELLER
                      </Button>

                      <Button
                        color='primary'
                        disabled={!VehicleDestinationCompanyAddress}
                        onClick={() => addAsOwner(form?.vehicleDestinationCompany?.name!, VehicleDestinationCompanyAddress!)}
                        size='small'
                        variant='text'
                      >
                        ADD AS OWNER
                      </Button>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell sx={ohioTitlesRowLabelCellSx}>Registered Importer</TableCell>

                    <TableCell>{form?.registeredImporter?.name ?? '-'}</TableCell>

                    <TableCell>
                      {form && form.registeredImporter && form.registeredImporter.addresses.length === 1
                        ? formatBasicAddress(form.registeredImporter.addresses[0])
                        : '-'}
                    </TableCell>

                    <TableCell align='right' sx={ohioTitlesPartyActionsCellSx}>
                      <Button
                        color='primary'
                        disabled={!form || !form.registeredImporter || form.registeredImporter.addresses.length !== 1}
                        onClick={() => addAsSeller(form?.registeredImporter?.name!, form?.registeredImporter?.addresses[0]!)}
                        size='small'
                        variant='text'
                        sx={theme => ({ mr: theme.spacing(4) })}
                      >
                        ADD AS SELLER
                      </Button>

                      <Button
                        color='primary'
                        disabled={!form || !form.registeredImporter || form.registeredImporter.addresses.length !== 1}
                        onClick={() =>
                          addAsOwner(
                            form?.registeredImporter?.name!,
                            form?.registeredImporter?.addresses[0]!,
                            form?.registeredImporter?.irsNumber,
                            form?.registeredImporter?.dealerNumber
                          )
                        }
                        size='small'
                        variant='text'
                      >
                        ADD AS OWNER
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Selected Vehicle Table:**

Displays the selected vehicle from the Ohio Titles Table.

**Included Vehicle Information Table:**

The following company details are displayed in the Selected Vehicle section:

CAN Ownership

Exporter Company

   - If the Exporter Company has multiple addresses, users can select another address from the dropdown menu.

Buyer Company (if one is associated with the vehicle)

   - If the Buyer Company has multiple addresses, users can select another address from the dropdown menu.

Title Destination Company

   - If the Title Destination Company has multiple addresses, users can select another address from the dropdown menu.

Vehicle Destination Company

   - If the Vehicle Destination Company has multiple addresses, users can select another address from the dropdown menu.

Registered Importer

**Add As Seller Button:**

Clicking the "Add As Seller" button duplicates the selected company’s information into the “Selected Vehicle – Ohio Title Application Packet” section on the Ohio Titles screen.

**Add As Owner Button:**

Clicking the "Add As Owner" button duplicates the selected company’s information into the “Selected Vehicle – Ohio Title Application Packet” section on the Ohio Titles screen.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
